/**
 * SessionDetail — side panel showing a session's details and state-contextual actions.
 *
 * Props:
 *   session       {Object|null}  selected session (null = closed)
 *   onClose       {()=>void}
 *   onRefresh     {()=>void}     called after a successful action
 *   showToast     {({message,variant})=>void}
 */

import { useState } from 'react';
import Dialog from '../ui/Dialog';
import Button from '../ui/Button';
import Field from '../ui/Field';
import Badge from '../ui/Badge';
import { SESSION_STATUS_LABELS, SESSION_STATUS_COLORS } from '../../constants/statusLabels';
import { getAllowedActions } from '../../utils/sessionActions';
import { getUserMessage, getFieldErrors } from '../../utils/apiErrors';
import apiService from '../../services/apiService';

// Report form initial state
const REPORT_INIT = {
  notions: '',
  difficulties: '',
  attention_score: '',
  homework: '',
};

const SessionDetail = ({ session, onClose, onRefresh, showToast }) => {
  const [submitting, setSubmitting] = useState(false);

  // Cancel reason dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');

  // Report form
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(REPORT_INIT);
  const [reportErrors, setReportErrors] = useState({});

  const open = Boolean(session);
  const actions = session ? getAllowedActions(session.status) : [];

  const handleClose = () => {
    setCancelOpen(false);
    setCancelReason('');
    setCancelReasonError('');
    setReportOpen(false);
    setReport(REPORT_INIT);
    setReportErrors({});
    onClose();
  };

  const handleStart = () => performActionDirect('start');
  const handleEnd = () => performActionDirect('end');

  const performActionDirect = async (action) => {
    if (!session) return;
    setSubmitting(true);
    try {
      if (action === 'start') {
        await apiService.startSession(session.contract_id, session.id);
      } else {
        await apiService.endSession(session.contract_id, session.id);
      }
      onRefresh();
      handleClose();
      showToast({ message: actionSuccessMessage(action), variant: 'success' });
    } catch (err) {
      showToast({ message: getUserMessage(err), variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) {
      setCancelReasonError('La raison est obligatoire.');
      return;
    }
    setCancelReasonError('');
    setSubmitting(true);
    try {
      await apiService.cancelSession(session.contract_id, session.id, {
        reason: cancelReason.trim(),
      });
      setCancelOpen(false);
      setCancelReason('');
      onRefresh();
      handleClose();
      showToast({ message: 'Séance annulée.', variant: 'success' });
    } catch (err) {
      const fieldErrors = getFieldErrors(err);
      if (fieldErrors.reason) {
        setCancelReasonError(fieldErrors.reason);
      } else {
        showToast({ message: getUserMessage(err), variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportChange = (field, value) => {
    setReport((prev) => ({ ...prev, [field]: value }));
    if (reportErrors[field]) {
      setReportErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleReportSubmit = async () => {
    if (!report.notions.trim()) {
      setReportErrors((prev) => ({ ...prev, notions: 'Champ requis.' }));
      return;
    }
    if (!report.attention_score) {
      setReportErrors((prev) => ({ ...prev, attention_score: 'Champ requis (1–5).' }));
      return;
    }
    setSubmitting(true);
    try {
      await apiService.reportSession(session.contract_id, session.id, {
        notions: report.notions.trim(),
        difficulties: report.difficulties.trim(),
        attention_score: Number(report.attention_score),
        homework: report.homework.trim(),
      });
      setReportOpen(false);
      setReport(REPORT_INIT);
      onRefresh();
      handleClose();
      showToast({ message: 'Compte rendu enregistré.', variant: 'success' });
    } catch (err) {
      const fieldErrors = getFieldErrors(err);
      if (Object.keys(fieldErrors).length) {
        setReportErrors(fieldErrors);
      } else {
        showToast({ message: getUserMessage(err), variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!session) return null;

  return (
    <>
      {/* Session detail panel as a Dialog */}
      <Dialog
        open={open && !cancelOpen && !reportOpen}
        onClose={handleClose}
        title="Détail de la séance"
        className="max-w-lg"
      >
        <div className="space-y-4">
          {/* Status + time */}
          <div className="flex items-center gap-3">
            <Badge
              status={session.status}
              labels={SESSION_STATUS_LABELS}
              colors={SESSION_STATUS_COLORS}
            />
            <span className="text-sm font-montserrat text-[#4A4A4A]">
              {session.date} {session.start_time ? `à ${session.start_time.slice(0, 5)}` : ''}
              {session.end_time ? ` – ${session.end_time.slice(0, 5)}` : ''}
            </span>
          </div>

          {/* Subject */}
          {session.subject && (
            <p className="text-sm font-montserrat text-[#4A4A4A]">
              <span className="font-semibold">Matière :</span> {session.subject.name}
            </p>
          )}

          {/* Student */}
          {session.student && (
            <p className="text-sm font-montserrat text-[#4A4A4A]">
              <span className="font-semibold">Élève :</span> {session.student.name}
            </p>
          )}

          {/* Cancel reason if cancelled */}
          {session.cancel_reason && (
            <p className="text-sm font-montserrat text-red-600">
              <span className="font-semibold">Raison d'annulation :</span> {session.cancel_reason}
            </p>
          )}

          {/* Report if available */}
          {session.report && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-xs font-montserrat font-semibold text-[#17386E] uppercase tracking-wide mb-2">
                Compte rendu
              </p>
              {session.report.notions && (
                <p className="text-sm font-montserrat text-[#4A4A4A]">
                  <span className="font-semibold">Notions :</span> {session.report.notions}
                </p>
              )}
              {session.report.difficulties && (
                <p className="text-sm font-montserrat text-[#4A4A4A]">
                  <span className="font-semibold">Difficultés :</span> {session.report.difficulties}
                </p>
              )}
              {session.report.attention_score !== undefined && (
                <p className="text-sm font-montserrat text-[#4A4A4A]">
                  <span className="font-semibold">Attention :</span> {session.report.attention_score}/5
                </p>
              )}
              {session.report.homework && (
                <p className="text-sm font-montserrat text-[#4A4A4A]">
                  <span className="font-semibold">Devoirs :</span> {session.report.homework}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          {actions.length > 0 && (
            <div className="flex gap-2 flex-wrap pt-2 border-t border-gray-100">
              {actions.includes('start') && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={submitting}
                  onClick={handleStart}
                >
                  Démarrer
                </Button>
              )}
              {actions.includes('end') && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={submitting}
                  onClick={handleEnd}
                >
                  Terminer
                </Button>
              )}
              {actions.includes('report') && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={submitting}
                  onClick={() => setReportOpen(true)}
                >
                  Compte rendu
                </Button>
              )}
              {actions.includes('cancel') && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={submitting}
                  onClick={() => setCancelOpen(true)}
                >
                  Annuler la séance
                </Button>
              )}
            </div>
          )}
        </div>
      </Dialog>

      {/* Cancel reason Dialog */}
      <Dialog
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelReason('');
          setCancelReasonError('');
        }}
        title="Annuler la séance"
      >
        <div className="space-y-4">
          <p className="text-sm font-montserrat text-[#4A4A4A]">
            Veuillez indiquer la raison de l'annulation.
          </p>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="cancel-reason"
              className="text-sm font-montserrat font-medium text-[#4A4A4A]"
            >
              Raison <span className="text-red-500">*</span>
            </label>
            <textarea
              id="cancel-reason"
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                if (cancelReasonError) setCancelReasonError('');
              }}
              className={[
                'w-full bg-[#F5F5F5] border rounded-xl py-3 px-4 font-montserrat text-sm text-[#4A4A4A]',
                'placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all resize-none',
                cancelReasonError ? 'border-red-500' : 'border-transparent focus:border-[#17386E]',
              ].join(' ')}
              placeholder="Ex: Indisponibilité imprévue…"
              disabled={submitting}
            />
            {cancelReasonError && (
              <p className="text-xs text-red-600">{cancelReasonError}</p>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              disabled={submitting}
              onClick={() => {
                setCancelOpen(false);
                setCancelReason('');
                setCancelReasonError('');
              }}
            >
              Retour
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={submitting || !cancelReason.trim()}
              onClick={handleCancelSubmit}
            >
              {submitting ? 'Annulation…' : "Confirmer l'annulation"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Report form Dialog */}
      <Dialog
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
          setReport(REPORT_INIT);
          setReportErrors({});
        }}
        title="Compte rendu de séance"
        className="max-w-lg"
      >
        <div className="space-y-4">
          <Field
            label="Notions abordées *"
            name="notions"
            value={report.notions}
            onChange={(e) => handleReportChange('notions', e.target.value)}
            error={reportErrors.notions}
            placeholder="Ex: Fractions, décomposition…"
            disabled={submitting}
          />
          <Field
            label="Difficultés rencontrées"
            name="difficulties"
            value={report.difficulties}
            onChange={(e) => handleReportChange('difficulties', e.target.value)}
            error={reportErrors.difficulties}
            placeholder="Ex: Problèmes de concentration…"
            disabled={submitting}
          />
          <div className="flex flex-col gap-1">
            <label
              htmlFor="attention_score"
              className="text-sm font-montserrat font-medium text-[#4A4A4A]"
            >
              Score d'attention (1–5) <span className="text-red-500">*</span>
            </label>
            <select
              id="attention_score"
              value={report.attention_score}
              onChange={(e) => handleReportChange('attention_score', e.target.value)}
              disabled={submitting}
              className={[
                'w-full bg-[#F5F5F5] border rounded-xl py-3 px-4 font-montserrat text-sm text-[#4A4A4A]',
                'focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all',
                reportErrors.attention_score ? 'border-red-500' : 'border-transparent focus:border-[#17386E]',
              ].join(' ')}
            >
              <option value="">— Sélectionner —</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {reportErrors.attention_score && (
              <p className="text-xs text-red-600">{reportErrors.attention_score}</p>
            )}
          </div>
          <Field
            label="Devoirs donnés"
            name="homework"
            value={report.homework}
            onChange={(e) => handleReportChange('homework', e.target.value)}
            error={reportErrors.homework}
            placeholder="Ex: Exercices 3 à 7 page 45…"
            disabled={submitting}
          />
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              disabled={submitting}
              onClick={() => {
                setReportOpen(false);
                setReport(REPORT_INIT);
                setReportErrors({});
              }}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={submitting}
              onClick={handleReportSubmit}
            >
              {submitting ? 'Enregistrement…' : 'Enregistrer le compte rendu'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

function actionSuccessMessage(action) {
  switch (action) {
    case 'start': return 'Séance démarrée.';
    case 'end': return 'Séance terminée.';
    default: return 'Action effectuée.';
  }
}

export default SessionDetail;
